// Cross-workspace sharing helpers extracted from src/lib/library.ts.
import { supabase } from "@/integrations/supabase/client";

/**
 * Add this library item to one or more additional workspaces. Existing
 * memberships are left untouched. The item's `workspace_id` (owning
 * workspace) is unchanged.
 */
export const shareLibraryItemToWorkspaces = async (
  libraryItemId: string,
  workspaceIds: string[],
) => {
  const targets = Array.from(new Set(workspaceIds.filter(Boolean)));
  if (targets.length === 0) return;
  const rows = targets.map((workspace_id) => ({ library_item_id: libraryItemId, workspace_id }));
  const { error } = await supabase
    .from("library_item_workspaces")
    .upsert(rows, { onConflict: "library_item_id,workspace_id", ignoreDuplicates: true });
  if (error) throw error;
};

/**
 * Replace the full set of workspace memberships for this library item.
 * The owning workspace is always retained even if missing from `nextIds`
 * (otherwise the item would become un-editable).
 */
export const setLibraryItemWorkspaces = async (
  libraryItemId: string,
  owningWorkspaceId: string,
  nextIds: string[],
) => {
  const desired = new Set(nextIds);
  desired.add(owningWorkspaceId);

  const { data: current, error: readError } = await supabase
    .from("library_item_workspaces")
    .select("workspace_id")
    .eq("library_item_id", libraryItemId);
  if (readError) throw readError;

  const currentIds = new Set((current ?? []).map((row: { workspace_id: string }) => row.workspace_id));
  const toAdd = Array.from(desired).filter((id) => !currentIds.has(id));
  const toRemove = Array.from(currentIds).filter((id) => !desired.has(id));

  if (toAdd.length) {
    const { error } = await supabase
      .from("library_item_workspaces")
      .upsert(
        toAdd.map((workspace_id) => ({ library_item_id: libraryItemId, workspace_id })),
        { onConflict: "library_item_id,workspace_id", ignoreDuplicates: true },
      );
    if (error) throw error;
  }
  if (toRemove.length) {
    const { error } = await supabase
      .from("library_item_workspaces")
      .delete()
      .eq("library_item_id", libraryItemId)
      .in("workspace_id", toRemove);
    if (error) throw error;
  }
};

/**
 * Move a library item to a new owning workspace. Used by Agency-mode
 * "Move to workspace…" (single-owner). The previous owning workspace is
 * removed from the visibility set; the new owning workspace is added.
 */
export const moveLibraryItemToWorkspace = async (
  libraryItemId: string,
  fromWorkspaceId: string,
  toWorkspaceId: string,
) => {
  if (fromWorkspaceId === toWorkspaceId) return;

  const { error: updateError } = await supabase
    .from("library_items")
    .update({ workspace_id: toWorkspaceId })
    .eq("id", libraryItemId);
  if (updateError) throw updateError;

  await shareLibraryItemToWorkspaces(libraryItemId, [toWorkspaceId]);

  const { error: deleteError } = await supabase
    .from("library_item_workspaces")
    .delete()
    .eq("library_item_id", libraryItemId)
    .eq("workspace_id", fromWorkspaceId);
  if (deleteError) throw deleteError;
};
